import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button, Icon, Toggle } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { buildBookEvents } from '~/app/pages/Transfers/_utils/book';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, RenderFields } from '~/components';
import { useAccountNumber, useBankAccount, useEntity } from '~/hooks';
import { useTransfer } from '~/hooks/useTransfers';
import { useNavigate } from '~/lib/navigation';
import { BookRepository, BookTransfer, convertTransferAmount } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { InfoForm, TransferPageHeader } from './_components/Layout';
import { Timeline } from './_components/Timeline';

interface Params {
  id: string;
}
interface ExtendedBookTransfer extends Omit<BookTransfer, 'senderBankAccountId'> {
  senderAccountNumberId: string;
  receiverAccountNumberId: string;
  senderBankAccountId: string;
  receiverBankAccountId: string;
  isIncoming?: boolean;
  createdBy?: {
    id: string;
    name: string;
  };
  reviews?: any[];
}

const formatData = (transfer: ExtendedBookTransfer): Record<string, unknown> => {
  const formatted: Record<string, unknown> = {};
  Object.entries(transfer).forEach(([key, value]) => {
    formatted[key] = value;
  });
  return formatted;
};

export const PageTransfersBook: FC = () => {
  const { isAuthorized } = useSessionStore();
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();

  const {
    response: senderBankAccount,
    isLoading: isSenderBankAccountLoading,
    createRequest: fetchSenderBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: receiverBankAccount,
    isLoading: isReceiverBankAccountLoading,
    createRequest: fetchReceiverBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: senderAccountNumber,
    isLoading: isSenderAccountNumberLoading,
    createRequest: fetchSenderAccountNumber,
  } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: receiverAccountNumber,
    isLoading: isReceiverAccountNumberLoading,
    createRequest: fetchReceiverAccountNumber,
  } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const { response: senderEntity, createRequest: fetchSenderEntity } = useEntity({
    onSuccess: () => setIsLoadingSenderEntity(false),
    onError: (error) => {
      setIsLoadingSenderEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const { response: receiverEntity, createRequest: fetchReceiverEntity } = useEntity({
    onSuccess: () => setIsLoadingReceiverEntity(false),
    onError: (error) => {
      setIsLoadingReceiverEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const [isLoadingSenderEntity, setIsLoadingSenderEntity] = useState(false);
  const [isLoadingReceiverEntity, setIsLoadingReceiverEntity] = useState(false);

  const {
    response: transfer,
    isLoading: isTransferLoading,
    createRequest: fetchTransfer,
  } = useTransfer<ExtendedBookTransfer>('book')({
    initialParams: { id },
    onSuccess: async (response) => {
      if (response.senderAccountNumberId) {
        fetchSenderAccountNumber({ id: response.senderAccountNumberId });
      }

      if (response.receiverAccountNumberId) {
        fetchReceiverAccountNumber({ id: response.receiverAccountNumberId });
      }

      try {
        if (response.senderBankAccountId) {
          const currentBankAccount = await fetchSenderBankAccount({ id: response.senderBankAccountId });

          if (!currentBankAccount) {
            return;
          }

          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchSenderEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingSenderEntity(true);
          }
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }

      try {
        if (response.receiverBankAccountId) {
          const currentBankAccount = await fetchReceiverBankAccount({ id: response.receiverBankAccountId });

          if (!currentBankAccount) {
            return;
          }

          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchReceiverEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingReceiverEntity(true);
          }
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }
    },

    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const formattedTransfer = useMemo(() => (transfer ? formatData(transfer) : undefined), [transfer]);

  const formMethods = useForm<ExtendedBookTransfer>();

  useEffect(() => {
    if (transfer) {
      formMethods.setValue('description', transfer.description || '-');
      formMethods.setValue('idempotencyKey', transfer.idempotencyKey || '-');
      formMethods.setValue('allowOverdraft', !!transfer.allowOverdraft);
    }
  }, [transfer]);

  const isDataLoading = useMemo(() => {
    return (
      isSenderAccountNumberLoading ||
      isReceiverAccountNumberLoading ||
      isSenderBankAccountLoading ||
      isReceiverBankAccountLoading ||
      isTransferLoading
    );
  }, [
    isSenderAccountNumberLoading,
    isReceiverAccountNumberLoading,
    isSenderBankAccountLoading,
    isReceiverBankAccountLoading,
    isTransferLoading,
  ]);

  const canClearOrCancelBookTransfer = useMemo(() => {
    return (
      !!senderBankAccount &&
      isAuthorized({ permission: 'bookTransfers', permissionLevel: 'write', bankAccount: senderBankAccount })
    );
  }, [senderBankAccount, isAuthorized]);

  const handleClearHold = useCallback(() => {
    if (!transfer) return;

    BookRepository.clear(transfer.id, {})
      .then(() => {
        fetchTransfer();
        addSuccessNotification({
          content: 'Book transfer hold cleared',
        });
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  }, [transfer, fetchTransfer, addSuccessNotification, addDangerNotification]);

  const handleCancelBook = useCallback(() => {
    if (!transfer) return;
    BookRepository.cancel(transfer.id)
      .then(() => {
        fetchTransfer();
        addSuccessNotification({
          content: 'Book transfer cancelled',
        });
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  }, [transfer, fetchTransfer, addSuccessNotification, addDangerNotification]);

  return (
    <>
      <PageActions>
        {transfer?.status === 'HOLD' && (
          <Button
            onClick={handleClearHold}
            variant="primary"
            size="small"
            icon={<Icon.Checkmark />}
            isDisabled={!canClearOrCancelBookTransfer}
          >
            Clear
          </Button>
        )}

        {transfer?.status === 'HOLD' && (
          <Button
            variant="danger"
            size="small"
            onClick={handleCancelBook}
            icon={<Icon.Cross />}
            isDisabled={!canClearOrCancelBookTransfer}
          >
            Cancel
          </Button>
        )}
      </PageActions>

      <Loading isLoading={isDataLoading}>
        {transfer && (
          <TransferPageHeader
            amount={transfer.amount!}
            createdAt={transfer.createdAt}
            isAdding={transfer ? convertTransferAmount('book', false) > 0 : undefined}
            direction={{ type: 'internal' }}
            status={transfer.status!}
            transferId={transfer.id!}
            transferType="book"
          />
        )}

        {senderAccountNumber &&
          receiverAccountNumber &&
          senderBankAccount &&
          receiverBankAccount &&
          transfer &&
          !isLoadingSenderEntity &&
          !isLoadingReceiverEntity &&
          (() => {
            // Get entity types safely
            const senderEntityType = senderEntity
              ? (senderEntity.type.toLowerCase() as 'person' | 'business')
              : undefined;

            const receiverEntityType = receiverEntity
              ? (receiverEntity.type.toLowerCase() as 'person' | 'business')
              : undefined;

            return (
              <Timeline
                entries={buildBookEvents({
                  transfer: { ...transfer },
                  senderAccount: {
                    senderAccountNumber: senderAccountNumber,
                    senderBankAccount: senderBankAccount,
                    senderEntityType: senderEntityType,
                  },
                  receiverAccount: {
                    receiverAccountNumber: receiverAccountNumber,
                    receiverBankAccount: receiverBankAccount,
                    receiverEntityType: receiverEntityType,
                  },
                })}
              />
            );
          })()}

        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<ExtendedBookTransfer>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },
                    {
                      id: 'idempotencyKey',
                      label: 'Idempotency Key',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || ''} isMonospaced />;
                      },
                    },
                    {
                      id: 'allowOverdraft',
                      label: 'Allow Overdraft',
                      children: ({ value, onChange }) => {
                        const isChecked = typeof value === 'boolean' ? value : false;
                        return <Toggle isChecked={isChecked} onCheckedChange={onChange} isDisabled />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Transfer Details"
              formattedData={formattedTransfer}
              rawData={transfer as Record<string, unknown> | undefined}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
